/* eslint-disable @typescript-eslint/no-explicit-any */
const RECAPTCHA_SCRIPT_BASE = "https://www.google.com/recaptcha/enterprise.js";

let scriptLoadPromise: Promise<void> | null = null;

function getRecaptchaApi(): any {
  const w = window as any;
  return w.grecaptcha && w.grecaptcha.enterprise;
}

export function loadRecaptchaScript(siteKey: string): Promise<void> {
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise<void>((resolve, reject) => {
    const api = getRecaptchaApi();
    if (api && api.execute) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = `${RECAPTCHA_SCRIPT_BASE}?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      getRecaptchaApi().ready(() => resolve());
    };

    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error("Failed to load reCAPTCHA Enterprise script"));
    };

    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

export function executeV3(siteKey: string, action: string): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!siteKey) {
      resolve("");
      return;
    }

    const api = getRecaptchaApi();
    if (!api || !api.execute) {
      resolve("");
      return;
    }

    api
      .execute(siteKey, { action })
      .then((token: string) => resolve(token))
      .catch((err: any) => reject(err));
  });
}

export interface RenderV2Options {
  onVerify?: (token: string) => void;
  onExpired?: () => void;
  onError?: () => void;
  action?: string;
}

export function renderV2(container: HTMLElement, siteKey: string, opts: RenderV2Options = {}): number {
  const api = getRecaptchaApi();
  if (!api || !api.render) {
    throw new Error("grecaptcha.enterprise not available — call loadRecaptchaScript() first");
  }

  const params: Record<string, unknown> = {
    sitekey: siteKey,
    callback: opts.onVerify,
    "expired-callback": opts.onExpired,
    "error-callback": opts.onError,
  };
  if (opts.action) {
    params.action = opts.action;
  }

  return api.render(container, params);
}

export function resetV2(widgetId: number): void {
  const api = getRecaptchaApi();
  if (api && api.reset) {
    api.reset(widgetId);
  }
}

/**
 * Force-hide the v2 challenge (bframe) overlay that grecaptcha appends directly
 * to document.body — outside the Vue tree. In the LINE LIFF webview the overlay
 * can be left orphaned on screen when the verify callback navigates away before
 * grecaptcha finishes closing it (iframe postMessage timing is slower there than
 * in desktop Chrome). We locate the challenge iframe and hide the top-level
 * wrapper grecaptcha mounts on <body>, so a stale dialog never stays stuck.
 *
 * Matched by the "bframe" src segment (the challenge iframe is always
 * .../recaptcha/.../bframe), which is locale-independent — the iframe `title`
 * is translated per UI language, so matching on it would silently miss e.g.
 * the Thai customer flow this fix targets.
 */
export function closeV2Challenge(): void {
  const iframes = document.querySelectorAll<HTMLIFrameElement>('iframe[src*="/recaptcha/"][src*="bframe"]');
  iframes.forEach((iframe) => {
    // walk up to the direct child of <body> — that's the overlay wrapper
    let wrapper: HTMLElement | null = iframe;
    while (wrapper && wrapper.parentElement && wrapper.parentElement !== document.body) {
      wrapper = wrapper.parentElement;
    }
    if (wrapper && wrapper.parentElement === document.body) {
      wrapper.style.visibility = "hidden";
      wrapper.style.opacity = "0";
      wrapper.style.pointerEvents = "none";
    }
  });
}
